/**
 * Interlinking helpers — shared SEO internal-link logic.
 *
 * BigLoads content has no explicit geo relation on blog posts, so we infer a
 * blog's target state from its slug/title (e.g. "towing-laws-in-texas" ->
 * Texas). This lets blog posts link out to their relevant state hub + city
 * money pages, giving Google (and users) a path from the high-traffic guide
 * content into the conversion pages.
 *
 * All matching is defensive: bad/empty data returns null/empty, never throws.
 */

/** Curated high-demand state slugs used as a fallback for non-geo (topical) blogs. */
export const TOP_STATE_SLUGS = [
  'trailer-rentals-texas',
  'trailer-rentals-florida',
  'trailer-rentals-california',
  'trailer-rentals-georgia',
  'trailer-rentals-arizona',
  'trailer-rentals-north-carolina',
];

const norm = s => (typeof s === 'string' ? s.trim().toLowerCase() : '');

/**
 * Match a blog to a state using its slug + title.
 * Uses LONGEST state-name match so "West Virginia" wins over "Virginia" and
 * "North Dakota" wins over any shorter partial. Returns the matched state
 * object (from the provided states list) or null.
 *
 * @param {{slug?:string,title?:string}} blog
 * @param {Array<{title?:string,slug?:string}>} states
 */
export const matchBlogState = (blog, states) => {
  if (!blog || !Array.isArray(states) || states.length === 0) return null;
  const hay = norm(`${blog.slug || ''} ${blog.title || ''}`);
  if (!hay) return null;
  let best = null;
  let bestLen = 0;
  states.forEach(st => {
    const name = norm(st && st.title);
    if (!name) return;
    // word-ish boundary check: the state name should appear as a token run,
    // not merely as a substring inside another word.
    const re = new RegExp(`(^|[^a-z])${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([^a-z]|$)`);
    if (re.test(hay) && name.length > bestLen) {
      best = st;
      bestLen = name.length;
    }
  });
  return best;
};

/**
 * Given a matched state and the full list of city categories, return that
 * state's cities. Cities carry a `state` relation object with its own slug.
 *
 * @param {{slug?:string}} state
 * @param {Array<{slug?:string,title?:string,state?:{slug?:string}}>} categories
 * @param {number} limit
 */
export const citiesForState = (state, categories, limit = 6) => {
  if (!state || !Array.isArray(categories)) return [];
  const stateSlug = norm(state.slug);
  if (!stateSlug) return [];
  return categories
    .filter(c => c && c.slug && norm(c.state && c.state.slug) === stateSlug)
    .slice(0, limit);
};

/**
 * Related guides: other blogs sharing the same tag, excluding the current blog.
 *
 * @param {{slug?:string,tag?:{name?:string}}} currentBlog
 * @param {Array} blogs
 * @param {number} limit
 */
export const relatedGuides = (currentBlog, blogs, limit = 4) => {
  if (!currentBlog || !Array.isArray(blogs)) return [];
  const curSlug = norm(currentBlog.slug);
  const curTag = norm(currentBlog.tag && currentBlog.tag.name);
  const sameTag = blogs.filter(
    b => b && b.slug && norm(b.slug) !== curSlug && curTag && norm(b.tag && b.tag.name) === curTag
  );
  // If not enough same-tag guides, top up with any other blogs (excluding self
  // and deduping) so the block is never empty on a lonely-tag post.
  const chosen = [...sameTag];
  if (chosen.length < limit) {
    const seen = new Set(chosen.map(b => norm(b.slug)));
    seen.add(curSlug);
    blogs.forEach(b => {
      if (chosen.length >= limit) return;
      if (!b || !b.slug) return;
      const s = norm(b.slug);
      if (seen.has(s)) return;
      seen.add(s);
      chosen.push(b);
    });
  }
  return chosen.slice(0, limit);
};

/** Nice human city label from a category (falls back to the slug). */
export const cityLabel = cat => {
  if (!cat) return '';
  const t = cat.title || '';
  // Titles are like "Trailer Rentals in Austin, Texas" / "Trailers for Rent in El Paso, Texas"
  const m = t.match(/in\s+(.+)$/i);
  return m ? m[1].trim() : t || cat.slug || '';
};

/** State display name from a state object. */
export const stateLabel = st => (st && st.title ? st.title : '');

/** Resolve a set of state objects from a list of slugs (for the topical fallback). */
export const statesFromSlugs = (slugs, states) => {
  if (!Array.isArray(slugs) || !Array.isArray(states)) return [];
  return slugs.map(sl => states.find(s => norm(s && s.slug) === norm(sl))).filter(Boolean);
};

/**
 * Other cities in the same state, excluding the current city. Used on a city
 * page to build the "nearby cities" internal-link block.
 *
 * @param {{slug?:string}} stateObj  the current city's state relation
 * @param {string} currentCitySlug
 * @param {Array} categories  all city categories
 * @param {number} limit
 */
export const nearbyCities = (stateObj, currentCitySlug, categories, limit = 8) => {
  if (!stateObj || !Array.isArray(categories)) return [];
  const stateSlug = norm(stateObj.slug);
  const cur = norm(currentCitySlug);
  if (!stateSlug) return [];
  return categories
    .filter(
      c =>
        c &&
        c.slug &&
        norm(c.slug) !== cur &&
        norm(c.state && c.state.slug) === stateSlug
    )
    .slice(0, limit);
};

/** Resolve a single state object from its slug. */
export const stateFromSlug = (slug, states) => {
  if (!slug || !Array.isArray(states)) return null;
  return states.find(s => norm(s && s.slug) === norm(slug)) || null;
};

/**
 * Pick a spread of OTHER states to cross-link from a state hub, so state pages
 * interlink each other instead of dead-ending laterally. Deterministic and
 * rotating: starts just after the current state (alphabetically) and wraps, so
 * different state pages surface different neighbors and the mesh fills out.
 *
 * @param {{slug?:string}} currentState
 * @param {Array} states
 * @param {number} limit
 */
export const otherStates = (currentState, states, limit = 8) => {
  if (!Array.isArray(states)) return [];
  const sorted = states
    .filter(s => s && s.slug && s.title)
    .slice()
    .sort((a, b) => String(a.title).localeCompare(String(b.title)));
  if (sorted.length === 0) return [];
  const curSlug = norm(currentState && currentState.slug);
  const foundIdx = sorted.findIndex(s => norm(s.slug) === curSlug);
  // If the current state isn't in the list (slug drift / new state), derive a
  // deterministic-but-varied start from the slug hash rather than defaulting to
  // index 0 (which would make every such page surface the SAME first neighbors
  // = a homogeneous-internal-links SEO regression).
  let startIdx;
  if (foundIdx >= 0) {
    startIdx = foundIdx;
  } else {
    let hash = 0;
    for (let i = 0; i < curSlug.length; i++) hash = (hash * 31 + curSlug.charCodeAt(i)) >>> 0;
    startIdx = hash % sorted.length;
  }
  const out = [];
  for (let i = 1; i <= sorted.length && out.length < limit; i++) {
    const cand = sorted[(startIdx + i) % sorted.length];
    if (norm(cand.slug) !== curSlug) out.push(cand);
  }
  return out;
};

/**
 * Find the blog guide most relevant to a given state — prefers a towing-laws
 * post for that state, then any blog we can pin to that state. Returns a single
 * blog or null. (Blogs have no geo relation; we reuse the slug/title matcher.)
 *
 * @param {{title?:string,slug?:string}} stateObj
 * @param {Array} blogs
 * @param {Array} states  full states list (for matchBlogState)
 */
export const stateGuideBlog = (stateObj, blogs, states) => {
  if (!stateObj || !Array.isArray(blogs)) return null;
  const stateName = norm(stateObj.title);
  if (!stateName) return null;
  // Candidate blogs whose inferred state == this state.
  const candidates = blogs.filter(b => {
    if (!b || !b.slug) return false;
    const st = matchBlogState(b, states);
    return st && norm(st.slug) === norm(stateObj.slug);
  });
  if (candidates.length === 0) return null;
  // Prefer a towing-laws tagged post (the canonical "[State] towing laws" guide).
  const towing = candidates.find(b => /tow/i.test((b.tag && b.tag.name) || '') || /towing-laws|towing-a-trailer/i.test(b.slug || ''));
  return towing || candidates[0];
};
